import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { tvSeriesList, categories } from '@/mocks/tv-series';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const series = tvSeriesList.find((s) => s.id === id);

  if (!series) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
          <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">未找到该剧集</h1>
        <p className="text-foreground-500 mb-6">该剧集可能已被移除或链接有误</p>
        <Link
          to="/"
          className="inline-flex px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === series.category)?.name || series.category;
  const relatedSeries = tvSeriesList
    .filter((s) => s.category === series.category && s.id !== series.id)
    .slice(0, 4);

  return (
    <>
      <SEOHead
        title={`${series.title} - ${series.subtitle} | 小草影院高清在线观看`}
        description={`小草影院提供${series.title}（${series.subtitle}）高清全集在线观看。${series.description}导演${series.director}，主演${series.cast.slice(0, 3).join('、')}等，共${series.episodes}集，${series.status}，高清画质即点即看。`}
        keywords={`小草影院,${series.title},${series.subtitle},${series.title}在线观看,${series.title}高清全集,${series.region}电视剧`}
        canonicalUrl={`https://www.xiaocaoyingyuan.com/article/${series.id}`}
        ogType="article"
        pageType="article"
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '小草影院首页', item: 'https://www.xiaocaoyingyuan.com/' },
            { '@type': 'ListItem', position: 2, name: `${categoryName}`, item: `https://www.xiaocaoyingyuan.com/category/${series.category}` },
            { '@type': 'ListItem', position: 3, name: series.title, item: `https://www.xiaocaoyingyuan.com/article/${series.id}` },
          ],
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: `${series.title} - ${series.subtitle}`,
          description: series.description,
          author: { '@type': 'Person', name: series.director },
          datePublished: `${series.year}-01-01`,
          image: series.posterImage,
          publisher: { '@type': 'Organization', name: '小草影院' },
        }}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-4">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryName, path: `/category/${series.category}` },
            { label: series.title },
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-8 mt-4">
          <div className="lg:w-[300px] shrink-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden">
              <img
                src={series.coverImage}
                alt={`${series.title} - ${series.subtitle}`}
                title={`${series.title} ${series.subtitle} 小草影院高清在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
              {series.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-4">{series.subtitle}</p>

            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="flex items-center gap-1 px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-medium">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-xs"></i>
                </div>
                {series.rating}
              </span>
              <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-sm">
                {series.region}
              </span>
              <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-sm">
                {series.year}
              </span>
              <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-sm">
                共{series.episodes}集
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                series.status === '更新中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
              }`}>
                {series.status}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {series.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="space-y-2 mb-6 text-sm">
              <div className="flex">
                <span className="text-foreground-500 w-16 shrink-0">导演：</span>
                <span className="text-foreground-800">{series.director}</span>
              </div>
              <div className="flex">
                <span className="text-foreground-500 w-16 shrink-0">主演：</span>
                <span className="text-foreground-800">{series.cast.join('、')}</span>
              </div>
              <div className="flex">
                <span className="text-foreground-500 w-16 shrink-0">更新：</span>
                <span className="text-foreground-800">{series.updateDay}</span>
              </div>
              <div className="flex">
                <span className="text-foreground-500 w-16 shrink-0">播放：</span>
                <span className="text-foreground-800">{(series.views / 10000).toFixed(0)}万次</span>
              </div>
            </div>

            <Link
              to={`/category/${series.category}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-circle-line text-sm"></i>
              </div>
              开始观看
            </Link>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">剧情简介</h2>
          <p className="text-sm text-foreground-700 leading-relaxed max-w-4xl">
            {series.longDescription}
          </p>
        </section>

        {series.episodeList.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              剧集列表
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {series.episodeList.map((ep) => (
                <button
                  key={ep.ep}
                  className="px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer text-left"
                >
                  <span className="font-medium">{ep.title}</span>
                  <span className="block text-xs text-foreground-400 mt-0.5">{ep.duration}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {relatedSeries.length > 0 && (
          <section className="mt-10 pb-10">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedSeries.map((s) => (
                <Link
                  key={s.id}
                  to={`/article/${s.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={s.coverImage}
                      alt={s.title}
                      title={`${s.title} 小草影院在线观看`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mt-2 truncate">{s.title}</h3>
                  <p className="text-xs text-foreground-500">{s.rating}分 · {s.episodes}集</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}